import NiceModal from "@ebay/nice-modal-react";
import { useMemo } from "react";
import { FormProvider } from "react-hook-form";

import Board from "components/Board";
import Box from "components/Box";
import Button from "components/Button";
import Empty from "components/Empty";
import AddAssistantModal from "components/modals/AddAssistantModal";
import SimpleModal from "components/modals/SimpleModal";
import Pagination from "components/Pagination";
import RawCheckbox from "components/RawCheckbox";
import EnhancedSearchField from "components/SearchField";
import Select from "components/Select";
import SortButton from "components/SortButton";
import Table from "components/Table";
import TitleHeading from "components/TitleHeading";
import { useGetGrades } from "hooks/apis/grade";
import { useDeleteUsers, useGetUsers } from "hooks/apis/user";
import useHandleParams from "hooks/useHandleParams";
import useSelection from "hooks/useSelection";
import format from "lib/format";
import { User } from "types/types";
import { addDashes } from "utils/utils";

type FormValues = {
  page: number;
  grade?: string;
  searchText?: string;
  sort: string;
};

const size = 20;

const SettingsAssistantPage = () => {
  const { methods, searchRef } = useHandleParams<FormValues>({
    defaultValues: {
      grade: "all",
      searchText: "",
      page: 1,
      sort: "createdDesc",
    },
  });

  const form = methods.watch();

  const { data, totalItems, currentPage, isFetching } = useGetUsers({
    size,
    ...form,
  });
  const { data: grades } = useGetGrades();

  const { onSelectAll, onSelectItem, selection, resetSelection } =
    useSelection<number>(data);

  const gradeOptions = useMemo(
    () => [
      { label: "전체 등급", value: "all" },
      ...(grades || []).map((grade) => ({
        label: grade.name,
        value: `${grade.id}`,
      })),
    ],
    [grades],
  );

  const gradeValues = useMemo(
    () =>
      grades
        ? [
            {
              label: "총 계정 수",
              value: grades
                .reduce((sum, { userCount }) => sum + userCount, 0)
                .toLocaleString(),
              unit: "개",
              className: "text-primary",
            },
            ...(grades || []).map((grade) => ({
              label: `${grade.name} 수`,
              value: grade.userCount.toLocaleString(),
              unit: "개",
              className: "text-black",
            })),
          ]
        : [
            {
              label: "",
              value: "",
              unit: "",
              className: "text-primary",
            },
          ],
    [grades],
  );

  const deleteUsersMutation = useDeleteUsers();

  const addAssistant = () => {
    NiceModal.show(AddAssistantModal);
  };

  const editAssistant = (user: User) => {
    NiceModal.show(AddAssistantModal, user);
  };

  const onDeleteUsers = () => {
    NiceModal.show(SimpleModal, {
      title: "계정 삭제",
      message: "선택한  계정을 삭제하시겠습니까?",
      submitText: "삭제",
    })
      .then(() => {
        deleteUsersMutation.mutate(
          { ids: selection },
          { onSuccess: resetSelection },
        );
      })
      .catch(() => {});
  };

  return (
    <FormProvider {...methods}>
      <main className="p-[1.875rem] pl-[15rem] 1350:pl-[16rem] 1400:pl-[16.875rem] w-full">
        <TitleHeading
          className="mb-[0.625rem]"
          title="조교관리"
          description="조교 관련 정보 화면입니다."
        />
        <Board title="조교 접속 현황" values={gradeValues}></Board>
        <div className="flex justify-between gap-[0.625rem] my-[0.625rem]">
          <div className="flex-[2]">
            <Select
              name="grade"
              options={gradeOptions}
              size="bigger"
              className="!w-[9.375rem]"
            />
          </div>
          <div className="flex-[4] flex gap-[0.625rem]">
            <EnhancedSearchField ref={searchRef} />
            <Button
              onClick={onDeleteUsers}
              disabled={!selection?.length}
              className="w-[9.375rem]"
              outline
              color="primary"
            >
              {selection?.length ? `삭제 (${selection.length})` : "삭제"}
            </Button>
            <Button
              onClick={addAssistant}
              color="primary"
              className="w-[9.375rem]"
            >
              계정 생성
            </Button>
          </div>
        </div>
        <Box className="!py-5 min-h-[calc(100%-20.188rem)] flex flex-col">
          <span className="text-16 font-medium text-secondary">
            총 {(totalItems ?? 0).toLocaleString()}개
          </span>
          <Table
            classNames={[
              "w-[4.8%] justify-center",
              "w-[6.67%]",
              "w-[9.3%]",
              "w-[9.3%]",
              "w-[16.8%]",
              "w-[16.4%]",
              "w-[16.4%]",
              "w-[11.9%]",
              "w-[8.67%] justify-center",
            ]}
          >
            <thead>
              <tr>
                <th>
                  <RawCheckbox
                    checked={
                      !!selection?.length && selection.length === data?.length
                    }
                    onChange={onSelectAll}
                  />
                </th>
                <th>번호</th>
                <th>등급</th>
                <th>이름</th>
                <th>휴대전화번호</th>
                <th>
                  <SortButton name="created" defaultValue="created">
                    등록일자
                  </SortButton>
                </th>
                <th>
                  <SortButton name="login" defaultValue="created">
                    최종 로그인 일시
                  </SortButton>
                </th>
                <th>대성 아이디</th>
                <th>수정</th>
              </tr>
            </thead>
            <tbody>
              {data?.map((user, index) => (
                <tr key={user.id}>
                  <td>
                    <RawCheckbox
                      checked={selection.includes(user.id)}
                      onChange={() => {
                        onSelectItem(user.id);
                      }}
                    />
                  </td>
                  <td>{totalItems - index - size * (currentPage - 1)}</td>
                  <td>{user.grade.name}</td>
                  <td>
                    {user.name}
                    {user ? `(${user.userId})` : ""}
                  </td>
                  <td>{addDashes(user.phone)}</td>
                  <td>{format(user.createdAt, "yyyy/MM/dd HH:mm:ss")}</td>
                  <td>{format(user.lastSeen, "yyyy/MM/dd HH:mm:ss")}</td>
                  <td>{user.daesungId}</td>
                  <td>
                    <button
                      onClick={() => editAssistant(user)}
                      type="button"
                      className="w-[3.125rem] h-[1.625rem] inline-flex justify-center items-center rounded-md bg-gray"
                    >
                      수정
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
          <Empty show={!isFetching && data && !data.length} />
          <Pagination total={totalItems} />
        </Box>
      </main>
    </FormProvider>
  );
};

export default SettingsAssistantPage;
